import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { Link } from "react-router-dom";
import type { ConversationResponse as Conversation } from "../../../api/generated";

interface RecentActivityProps {
  conversations: Conversation[];
  limit?: number;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffSec = Math.round((then - now) / 1000);

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  for (const [unit, seconds] of units) {
    if (Math.abs(diffSec) >= seconds) {
      return rtf.format(Math.round(diffSec / seconds), unit);
    }
  }
  return "just now";
}

export function RecentActivity({
  conversations,
  limit = 5,
}: RecentActivityProps) {
  const items = conversations.slice(0, limit);

  if (items.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography gutterBottom sx={{
          color: "text.secondary"
        }}>
          No conversations yet
        </Typography>
        <Button variant="outlined" component={Link} to="/chat" size="small">
          Start a Conversation
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <List disablePadding>
        {items.map((conv) => (
          <ListItemButton
            key={conv.id}
            component={Link}
            to={`/chat/${conv.id}`}
            sx={{ borderRadius: 1, mb: 0.5 }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {conv.title || "Untitled"}
                  </Typography>
                  <Chip
                    icon={
                      conv.mode === "pre_call" ? (
                        <SearchIcon />
                      ) : (
                        <AssessmentIcon />
                      )
                    }
                    label={conv.mode === "pre_call" ? "Pre-call" : "Analysis"}
                    size="small"
                    color={conv.mode === "post_call" ? "primary" : "default"}
                    variant="outlined"
                  />
                </Box>
              }
              secondary={relativeTime(conv.updated_at)}
            />
          </ListItemButton>
        ))}
      </List>
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Button component={Link} to="/chat" state={{ showHistory: true }} size="small">
          View All
        </Button>
      </Box>
    </Box>
  );
}
